import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowDown, ArrowUp, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
    CircleAlert, Download, FileJson, FileSpreadsheet, FileText, Loader2,
    Printer, RefreshCw, Search, SlidersHorizontal,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import styles from './CollectionReport.module.css';
import { useStore } from '../../../../context/StoreContext';
import { useAuth } from '../../../../context/AuthContext'; 

const configuredApiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const API_BASE = configuredApiBase.replace(/\/api\/v1(?:\/[^/]+)?\/?$/, '').replace(/\/+$/, '');

const moneyFormatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
const numberValue = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(String(value).replace(/[₹,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};
const formatMoney = (value) => moneyFormatter.format(numberValue(value));
const formatPdfMoney = (value) => numberValue(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const textValue = (value, fallback = '') => (value === null || value === undefined) ? fallback : String(value);
const listValue = (value) => Array.isArray(value) ? value : (value ? [value] : []);
const readableDate = (value) => {
    if (!value) return '—';
    if (typeof value === 'string' && !value.includes('T')) return value;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
};
const dateSortValue = (value) => Number.isNaN(new Date(value || '').getTime()) ? 0 : new Date(value || '').getTime();

const getStoreDetails = (store) => {
    if (!store) return { name: '', address: '', phone: '', gstin: '' };
    const name = textValue(store?.name ?? store?.storeName ?? store?.businessName, 'Store');
    const addressParts = [
        textValue(store?.address ?? store?.addressLine1 ?? store?.street ?? store?.shopAddress),
        textValue(store?.addressLine2),
        textValue(store?.area ?? store?.locality),
        textValue(store?.city),
        textValue(store?.state),
        textValue(store?.pincode ?? store?.zip ?? store?.postalCode)
    ].filter(Boolean);
    return { name, address: addressParts.join(', '), phone: textValue(store?.phone ?? store?.contactNumber ?? store?.mobile ?? store?.storePhone), gstin: textValue(store?.gstin ?? store?.GSTIN ?? store?.gstNumber) };
};

const normalizeServices = (row) => listValue(row?.serviceDetails ?? row?.services ?? row?.items ?? row?.lineItems ?? (row?.service ? [row.service] : [])).map((service, index) => {
    if (typeof service === 'string' || typeof service === 'number') return { name: String(service), quantity: 1, amount: '', gst: '', total: '', staff: '', duration: '' };
    return {
        name: textValue(service?.name ?? service?.title ?? service?.serviceName ?? service?.itemName, 'Service ' + (index + 1)),
        quantity: numberValue(service?.quantity ?? service?.qty) || 1, amount: service?.amount ?? service?.price ?? service?.subtotal ?? '',
        gst: service?.gst ?? service?.gstAmount ?? '', total: service?.total ?? service?.totalAmount ?? '', staff: textValue(service?.staff ?? service?.staffName), duration: service?.duration ?? ''
    };
});

const normalizePayments = (row) => {
    const payments = listValue(row?.payments ?? row?.paymentSplits ?? row?.paymentDetails);
    if (!payments.length && (row?.paymentMode || row?.mode)) return [{ mode: textValue(row.paymentMode ?? row.mode, 'Other'), amount: numberValue(row?.collectedAmount ?? row?.paidAmount), reference: '' }];
    return payments.map((payment) => typeof payment === 'string' || typeof payment === 'number' ? { mode: String(payment), amount: 0, reference: '' } : { mode: textValue(payment?.mode ?? payment?.method ?? payment?.paymentMode, 'Other'), amount: numberValue(payment?.amount ?? payment?.paidAmount ?? payment?.value), reference: textValue(payment?.reference ?? payment?.transactionId ?? payment?.refNo) });
};

const normalizeStaff = (row) => listValue(row?.staff ?? row?.staffNames ?? row?.servedBy ?? row?.consultBy).map((staff) => typeof staff === 'string' || typeof staff === 'number' ? String(staff) : textValue(staff?.name ?? staff?.fullName ?? staff?.staffName, 'Unassigned')).filter(Boolean);

const normalizeRow = (row, index) => {
    const customer = row?.customer ?? row?.client ?? row?.customerDetails ?? {};
    const serviceDetails = normalizeServices(row);
    const payments = normalizePayments(row);
    const totalAmount = numberValue(row?.totalAmount ?? row?.grandTotal ?? row?.total ?? row?.netAmount);
    
    const netGst = numberValue(row?.gst ?? row?.totalGst ?? row?.gstAmount);
    const netTaxable = numberValue(row?.taxable ?? row?.taxableAmount);

    const paymentBreakup = payments.reduce((res, p) => {
        const mode = textValue(p.mode).trim().toLowerCase();
        if (mode.includes('cash')) res.cash += p.amount;
        else if (mode.includes('upi') || mode === 'online' || mode.includes('phonepe') || mode.includes('gpay') || mode.includes('paytm')) res.upi += p.amount;
        else if (mode.includes('member') || mode.includes('package') || mode.includes('plan') || mode.includes('prepaid')) res.membership += p.amount;
        else if (mode.includes('card') || mode.includes('credit') || mode.includes('debit')) res.card += p.amount;
        else if (mode.includes('wallet')) res.wallet += p.amount;
        else res.other += p.amount;
        return res;
    }, { cash: 0, upi: 0, card: 0, membership: 0, wallet: 0, other: 0 });

    const cashAmount = numberValue(row?.cashAmount ?? row?.cash) || paymentBreakup.cash;
    const upiAmount = numberValue(row?.upiAmount ?? row?.upi) || paymentBreakup.upi;
    const cardAmount = numberValue(row?.cardAmount ?? row?.card) || paymentBreakup.card;
    const membershipAmount = numberValue(row?.membershipAmount ?? row?.prepaidMembershipAmount ?? row?.membership?.amount ?? row?.prepaidAmount) || paymentBreakup.membership;
    const walletAmount = numberValue(row?.walletAmount ?? row?.wallet) || paymentBreakup.wallet;
    const otherAmount = numberValue(row?.otherPaymentAmount ?? row?.otherPayment) || paymentBreakup.other;
    const actualCollectedAmount = numberValue(row?.collectedAmount) || (cashAmount + upiAmount + cardAmount + otherAmount);

    const rawDate = row?.date ?? row?.createdAt ?? row?.invoiceDate;
    const rawStatus = row?.status ?? row?.paymentStatus ?? 'Paid';
    const membershipValue = row?.membership?.name ?? row?.membershipName ?? row?.membershipPlan ?? row?.membership ?? '';

    return {
        ...row, _key: textValue(row?.id ?? row?._id ?? row?.invoiceNumber, 'row-' + index) + '-' + index, id: textValue(row?.id ?? row?.invoiceNumber ?? row?.receiptNo, '—'),
        date: readableDate(rawDate), rawDate, customer: { name: textValue(customer?.name ?? row?.customerName, 'Walk-in customer'), phone: textValue(customer?.phone ?? row?.customerPhone, '—') },
        serviceDetails, services: serviceDetails.map(s => s.name), staff: normalizeStaff(row),
        payments, cashAmount, upiAmount, cardAmount, membershipAmount, walletAmount, otherAmount, actualCollectedAmount,
        membership: textValue(membershipValue) || (membershipAmount > 0 ? 'Membership / prepaid used' : ''), status: textValue(rawStatus, 'Paid'), 
        taxable: netTaxable, gst: netGst, totalAmount, collectedAmount: actualCollectedAmount
    };
};

const lower = (value) => textValue(value).toLowerCase();
const DEFAULT_COLUMNS = ['date', 'receipt', 'customer', 'services', 'cash', 'upi', 'card', 'membershipPayment', 'gst', 'collected', 'total', 'status'];

const COLUMN_DEFINITIONS = [
    { key: 'date', label: 'Date', sortable: true }, { key: 'receipt', label: 'Receipt no.', sortable: true },
    { key: 'customer', label: 'Client details', sortable: true }, { key: 'services', label: 'Services', sortable: true },
    { key: 'staff', label: 'Staff', sortable: true }, { key: 'cash', label: 'Cash', sortable: true, align: 'right' },
    { key: 'upi', label: 'UPI', sortable: true, align: 'right' }, { key: 'card', label: 'Card', sortable: true, align: 'right' },
    { key: 'membershipPayment', label: 'Advance Adjusted', sortable: true, align: 'right' }, { key: 'wallet', label: 'Wallet', sortable: true, align: 'right' },
    { key: 'other', label: 'Other', sortable: true, align: 'right' }, { key: 'gst', label: 'Net GST', sortable: true, align: 'right' },
    { key: 'collected', label: 'Net Collected', sortable: true, align: 'right' }, { key: 'total', label: 'Gross Billed', sortable: true, align: 'right' },
    { key: 'status', label: 'Status', sortable: true, align: 'center' }, { key: 'payments', label: 'Payment references', sortable: false }
];

export default function CollectionReport({ dateFilter, onDataLoaded, onTotalsCalculated }) {
    const { activeStore } = useStore();
    const { user, hasPermission } = useAuth(); 
    
    const storeId = activeStore?._id || activeStore?.id || (typeof localStorage !== 'undefined' ? localStorage.getItem('activeStoreId') : '');
    const storeDetails = useMemo(() => getStoreDetails(activeStore), [activeStore]);

    const [collectionData, setCollectionData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [query, setQuery] = useState('');
    const [paymentMode, setPaymentMode] = useState('all');
    const [serviceFilter, setServiceFilter] = useState('all');
    const [staffFilter, setStaffFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sort, setSort] = useState({ key: 'date', direction: 'desc' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [showColumns, setShowColumns] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
    const [showExport, setShowExport] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [draggedColumn, setDraggedColumn] = useState(null);

    const columnControlRef = useRef(null);
    const exportControlRef = useRef(null);

    const canExport = hasPermission('reports', 'export');
    const exportConstraints = user?.policies?.reports?.constraints || {};
    const canExcel = exportConstraints.allowExcelExport !== false;
    const canPdf = exportConstraints.allowPdfExport !== false;
    const canCsv = exportConstraints.allowCsvExport !== false;
    const canPrint = exportConstraints.allowPrint !== false;
    const hasAnyExportOption = canExcel || canPdf || canCsv || canPrint;

    const fetchReport = useCallback(async (signal) => {
        if (!storeId || storeId === 'null' || storeId === 'undefined') {
            setCollectionData([]); setError('Select a store to view its collection report.'); setIsLoading(false); return;
        }
        if(!dateFilter?.startDate || !dateFilter?.endDate) return;

        setIsLoading(true); setError('');
        try {
            const token = typeof localStorage !== 'undefined' ? localStorage.getItem('smiify_token') : '';
            
            // 🚀 DYNAMIC URL PARAMS BASED ON GLOBAL DATE FILTER
            const params = new URLSearchParams({ 
                storeId: String(storeId), 
                paymentMode: paymentMode || 'all',
                period: dateFilter.value || 'today',
                startDate: dateFilter.startDate,
                endDate: dateFilter.endDate
            });

            const response = await fetch(API_BASE + '/api/v1/smiify/sm/analytics/collection-report?' + params.toString(), {
                headers: token ? { Authorization: 'Bearer ' + token } : {}, signal
            });
            const result = await response.json();
            if (!response.ok || result?.success === false) throw new Error(result?.message || 'Unable to load report.');
            const payload = result?.data?.rows ?? result?.data ?? [];
            setCollectionData(Array.isArray(payload) ? payload.map(normalizeRow) : []);
        } catch (err) {
            if (err?.name === 'AbortError') return;
            setError(err?.message || 'Unable to load collection report.');
        } finally {
            if (!signal.aborted) setIsLoading(false);
        }
    }, [paymentMode, dateFilter, refreshKey, storeId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchReport(controller.signal);
        return () => controller.abort();
    }, [fetchReport]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (columnControlRef.current && !columnControlRef.current.contains(event.target)) setShowColumns(false);
            if (exportControlRef.current && !exportControlRef.current.contains(event.target)) setShowExport(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    useEffect(() => { setPage(1); }, [query, serviceFilter, staffFilter, statusFilter, pageSize, dateFilter, paymentMode]);

    const serviceOptions = useMemo(() => Array.from(new Set(collectionData.flatMap(r => r.services))).sort(), [collectionData]);
    const staffOptions = useMemo(() => Array.from(new Set(collectionData.flatMap(r => r.staff))).sort(), [collectionData]);
    const statusOptions = useMemo(() => Array.from(new Set(collectionData.map(r => r.status).filter(Boolean))).sort(), [collectionData]);

    const filteredRows = useMemo(() => {
        const search = lower(query.trim());
        const matching = collectionData.filter((row) => {
            const haystack = [row.id, row.date, row.customer.name, row.customer.phone, row.services.join(' '), row.staff.join(' '), row.payments.map(p => p.mode).join(' '), row.status].join(' ').toLowerCase();
            return (!search || haystack.includes(search))
                && (paymentMode === 'all' || row[paymentMode + 'Amount'] > 0)
                && (serviceFilter === 'all' || row.services.includes(serviceFilter))
                && (staffFilter === 'all' || row.staff.includes(staffFilter))
                && (statusFilter === 'all' || row.status === statusFilter);
        });

        const getSortValue = (row) => {
            if (sort.key === 'date') return dateSortValue(row.rawDate);
            if (sort.key === 'receipt') return lower(row.id);
            if (sort.key === 'customer') return lower(row.customer.name);
            if (sort.key === 'services') return lower(row.services.join(', '));
            if (sort.key === 'staff') return lower(row.staff.join(', '));
            if (['cash', 'upi', 'card', 'wallet', 'other', 'gst', 'total'].includes(sort.key)) return row[sort.key === 'total' ? 'totalAmount' : sort.key === 'gst' ? 'gst' : sort.key + 'Amount'];
            if (sort.key === 'membershipPayment') return row.membershipAmount;
            if (sort.key === 'collected') return row.actualCollectedAmount;
            if (sort.key === 'status') return lower(row.status);
            return '';
        };

        return [...matching].sort((a, b) => {
            const left = getSortValue(a), right = getSortValue(b);
            if (left === right) return 0;
            return (left > right ? 1 : -1) * (sort.direction === 'asc' ? 1 : -1);
        });
    }, [collectionData, paymentMode, query, serviceFilter, staffFilter, statusFilter, sort]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const visibleRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);
    const firstVisibleRow = filteredRows.length ? (page - 1) * pageSize + 1 : 0;
    const lastVisibleRow = Math.min(page * pageSize, filteredRows.length);

    const totals = useMemo(() => {
        const res = { totalCash: 0, totalUpi: 0, totalCard: 0, totalMembership: 0, totalWalletUsed: 0, totalOther: 0, totalTaxable: 0, totalGst: 0, grandTotal: 0, actualNetCollected: 0, totalReceipts: filteredRows.length };
        filteredRows.forEach((row) => {
            res.totalTaxable += row.taxable; res.totalGst += row.gst; res.grandTotal += row.totalAmount;
            res.totalCash += row.cashAmount; res.totalUpi += row.upiAmount; res.totalCard += row.cardAmount;
            res.totalMembership += row.membershipAmount; res.totalWalletUsed += row.walletAmount; res.totalOther += row.otherAmount;
        });
        res.actualNetCollected = res.totalCash + res.totalUpi + res.totalCard + res.totalOther;
        return res;
    }, [filteredRows]);

    useEffect(() => { if (onDataLoaded) onDataLoaded(filteredRows); }, [filteredRows, onDataLoaded]);
    useEffect(() => { if (onTotalsCalculated) onTotalsCalculated(totals); }, [onTotalsCalculated, totals]);

    const orderedColumnDefinitions = visibleColumns.map(key => COLUMN_DEFINITIONS.find(c => c.key === key)).filter(Boolean);

    const getDynamicExportData = () => {
        return filteredRows.map(row => {
            const exportObj = {};
            orderedColumnDefinitions.forEach(col => {
                if (col.key === 'date') exportObj[col.label] = row.date;
                if (col.key === 'receipt') exportObj[col.label] = row.id;
                if (col.key === 'customer') exportObj[col.label] = `${row.customer.name} - ${row.customer.phone}`;
                if (col.key === 'services') exportObj[col.label] = row.services.join(', ');
                if (col.key === 'staff') exportObj[col.label] = row.staff.join(', ');
                if (col.key === 'cash') exportObj[col.label] = row.cashAmount;
                if (col.key === 'upi') exportObj[col.label] = row.upiAmount;
                if (col.key === 'card') exportObj[col.label] = row.cardAmount;
                if (col.key === 'membershipPayment') exportObj[col.label] = row.membershipAmount;
                if (col.key === 'wallet') exportObj[col.label] = row.walletAmount;
                if (col.key === 'other') exportObj[col.label] = row.otherAmount;
                if (col.key === 'gst') exportObj[col.label] = row.gst;
                if (col.key === 'collected') exportObj[col.label] = row.actualCollectedAmount;
                if (col.key === 'total') exportObj[col.label] = row.totalAmount;
                if (col.key === 'status') exportObj[col.label] = row.status;
                if (col.key === 'payments') exportObj[col.label] = row.payments.map(p => `${p.mode}: ${p.amount}`).join(', ');
            });
            return exportObj;
        });
    };

    const handleExcelExport = () => {
        if (!filteredRows.length) return;
        setIsExporting(true); setShowExport(false);
        setTimeout(() => {
            const data = getDynamicExportData();
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(data);
            XLSX.utils.book_append_sheet(wb, ws, "Collection Report");
            XLSX.writeFile(wb, `Collection_Report_${new Date().getTime()}.xlsx`);
            setIsExporting(false);
        }, 300);
    };

    const handleCSVExport = () => {
        if (!filteredRows.length) return;
        setIsExporting(true); setShowExport(false);
        setTimeout(() => {
            const data = getDynamicExportData();
            const ws = XLSX.utils.json_to_sheet(data);
            const csv = XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Collection_Report_${new Date().getTime()}.csv`;
            link.click();
            setIsExporting(false);
        }, 300);
    };

    const handlePDFExport = () => {
        if (!filteredRows.length) return;
        setIsExporting(true); setShowExport(false);
        
        setTimeout(() => {
            const doc = new jsPDF('landscape');
            const pageWidth = doc.internal.pageSize.getWidth();
            const brandPrimary = [103, 61, 230]; 
            const textDark = [15, 23, 42];
            const beautifulDate = new Intl.DateTimeFormat('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
            }).format(new Date());

            doc.setFillColor(...brandPrimary);
            doc.rect(0, 0, pageWidth, 32, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(storeDetails.name || 'Testing Account', 14, 14);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(storeDetails.address || 'Address', 14, 21);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('COLLECTION REPORT', pageWidth - 14, 14, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(`Period: ${dateFilter.label || 'Custom'}`, pageWidth - 14, 21, { align: 'right' });
            doc.text(`Generated: ${beautifulDate}`, pageWidth - 14, 27, { align: 'right' });

            let cursorY = 42;
            doc.setTextColor(...textDark);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('Collection Summary', 14, cursorY);

            autoTable(doc, {
                startY: cursorY + 4,
                head: [['Net Revenue (Rs)', 'Net GST Collected (Rs)', 'Cash', 'UPI / Card', 'Advance Adjusted', 'Gross Billed', 'Actual Collected']],
                body: [[formatPdfMoney(totals.totalTaxable), formatPdfMoney(totals.totalGst), formatPdfMoney(totals.totalCash), formatPdfMoney(totals.totalUpi + totals.totalCard), formatPdfMoney(totals.totalWalletUsed + totals.totalMembership), formatPdfMoney(totals.grandTotal), formatPdfMoney(totals.actualNetCollected)]],
                theme: 'plain',
                headStyles: { fillColor: [248, 249, 250], textColor: [100, 116, 139], fontSize: 8, fontStyle: 'bold', halign: 'center' },
                bodyStyles: { textColor: textDark, fontSize: 10, fontStyle: 'bold', halign: 'center', cellPadding: 4 },
                margin: { left: 14, right: 14 }
            });

            const tableStartY = doc.lastAutoTable.finalY + 12;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('Transaction Details', 14, tableStartY);

            const moneyCols = ['cash', 'upi', 'card', 'membershipPayment', 'wallet', 'other', 'gst', 'collected', 'total'];
            const head = [orderedColumnDefinitions.map(col => col.label)];

            const body = filteredRows.map(row => {
                return orderedColumnDefinitions.map(col => {
                    if (moneyCols.includes(col.key)) {
                        const val = col.key === 'total' ? row.totalAmount : col.key === 'collected' ? row.actualCollectedAmount : col.key === 'membershipPayment' ? row.membershipAmount : col.key === 'gst' ? row.gst : row[col.key + 'Amount'];
                        return formatPdfMoney(val);
                    }
                    if (col.key === 'date') return row.date;
                    if (col.key === 'receipt') return row.id;
                    if (col.key === 'customer') return `${row.customer.name}\n${row.customer.phone !== '—' ? `(${row.customer.phone})` : ''}`;
                    if (col.key === 'services') return row.services.join(', ');
                    if (col.key === 'staff') return row.staff.join(', ');
                    if (col.key === 'status') return row.status;
                    if (col.key === 'payments') return row.payments.map(p => `${p.mode}: ${p.amount}`).join(', ');
                    return '';
                });
            });

            const columnStyles = {};
            orderedColumnDefinitions.forEach((col, idx) => {
                if (col.align === 'right') columnStyles[idx] = { halign: 'right' };
                if (col.align === 'center') columnStyles[idx] = { halign: 'center' };
                if (col.key === 'total' || col.key === 'collected') columnStyles[idx] = { ...columnStyles[idx], fontStyle: 'bold', textColor: col.key === 'collected' ? [20, 137, 103] : textDark };
            });

            autoTable(doc, {
                head, body, startY: tableStartY + 4, margin: { left: 10, right: 10 }, theme: 'grid',
                styles: { fontSize: 7.5, cellPadding: 3, textColor: textDark, lineColor: [226, 232, 240], lineWidth: 0.1 },
                headStyles: { fillColor: brandPrimary, textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 253, 255] }, columnStyles,
                didDrawPage: () => {
                    doc.setFontSize(8); doc.setTextColor(100, 116, 139);
                    doc.text(`Page ${doc.getNumberOfPages()}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 8);
                }
            });

            doc.save(`Collection_Report_${new Date().getTime()}.pdf`);
            setIsExporting(false);
        }, 300);
    };

    const setSortKey = (key) => setSort(c => c.key === key ? { key, direction: c.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' });
    const toggleColumn = (key) => setVisibleColumns(c => c.includes(key) ? (c.length === 1 ? c : c.filter(col => col !== key)) : [...c, key]);
    const handleColumnDragStart = (e, key) => { setDraggedColumn(key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); };
    const handleColumnDrop = (e, targetKey) => {
        e.preventDefault();
        const sourceKey = draggedColumn || e.dataTransfer.getData('text/plain');
        if (!sourceKey || sourceKey === targetKey) return;
        setVisibleColumns(current => {
            const next = [...current];
            const srcIdx = next.indexOf(sourceKey), tgtIdx = next.indexOf(targetKey);
            if (srcIdx === -1 || tgtIdx === -1) return current;
            next.splice(srcIdx, 1); next.splice(tgtIdx, 0, sourceKey);
            return next;
        });
        setDraggedColumn(null);
    };

    const resetFilters = () => {
        setQuery(''); setPaymentMode('all'); setServiceFilter('all'); setStaffFilter('all'); setStatusFilter('all');
        setSort({ key: 'date', direction: 'desc' }); setVisibleColumns(DEFAULT_COLUMNS); setDraggedColumn(null); setPage(1);
    };

    const renderCell = (row, key) => {
        if (key === 'date') return <span className={styles.dateCell}>{row.date}</span>;
        if (key === 'receipt') return <span className={styles.invoiceLink}>{row.id}</span>;
        if (key === 'customer') return <div className={styles.clientCell}><strong>{row.customer.name}</strong><span>{row.customer.phone}</span></div>;
        if (key === 'services') return (
            <div className={styles.itemCell}>
                <div className={styles.serviceList}>
                    {row.serviceDetails.length ? row.serviceDetails.map((service, idx) => (
                        <div className={styles.serviceLine} key={idx}><span>{service.name}</span>{service.amount !== '' && <strong>{formatMoney(service.amount)}</strong>}</div>
                    )) : <span className={styles.emptyValue}>No service recorded</span>}
                </div>
                {row.membership && <span className={styles.membershipBadge}>{row.membership}</span>}
            </div>
        );
        if (key === 'staff') return <div className={styles.staffCell}>{row.staff.length ? row.staff.map((st, i) => <span className={styles.staffPill} key={i}>{st}</span>) : <span className={styles.emptyValue}>Unassigned</span>}</div>;
        if (key === 'payments') return (
            <div className={styles.paymentCell}>
                {row.payments.length ? row.payments.map((p, i) => (
                    <div className={styles.paymentLine} key={i}><span className={styles.paymentMode}>{p.mode}</span><strong>{formatMoney(p.amount)}</strong></div>
                )) : <span className={styles.emptyValue}>No payment split</span>}
            </div>
        );
        if (key === 'cash') return <strong className={styles.paymentAmount}>{formatMoney(row.cashAmount)}</strong>;
        if (key === 'upi') return <strong className={styles.paymentAmount}>{formatMoney(row.upiAmount)}</strong>;
        if (key === 'card') return <strong className={styles.paymentAmount}>{formatMoney(row.cardAmount)}</strong>;
        if (key === 'membershipPayment') return <strong className={styles.membershipAmount} style={{color: '#b45309'}}>{formatMoney(row.membershipAmount)}</strong>;
        if (key === 'wallet') return <strong className={styles.paymentAmount}>{formatMoney(row.walletAmount)}</strong>;
        if (key === 'other') return <strong className={styles.paymentAmount}>{formatMoney(row.otherAmount)}</strong>;
        if (key === 'gst') return <span className={styles.gstText}>{row.gst ? formatMoney(row.gst) : '—'}</span>;
        if (key === 'collected') return <strong className={styles.collectedCell}>{formatMoney(row.actualCollectedAmount)}</strong>;
        if (key === 'total') return <strong className={styles.moneyCell}>{formatMoney(row.totalAmount)}</strong>;
        if (key === 'status') {
            const status = lower(row.status);
            const tone = status.includes('cancel') || status.includes('refund') ? 'danger' : status.includes('pending') || status.includes('due') ? 'warning' : 'success';
            return <span className={styles.statusBadge} data-tone={tone}>{row.status}</span>;
        }
        return '—';
    };

    if (isLoading) {
        return (
            <section className={styles.reportPanel}>
                <div className={styles.loadingState}>
                    <Loader2 size={25} className={styles.spin} />
                    <strong>Loading collection report</strong>
                    <span>Preparing your sheet and settlement totals…</span>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.reportPanel}>
            {error ? (
                <div className={styles.errorState} role="alert">
                    <CircleAlert size={19} />
                    <div><strong>{error}</strong><span>Check the active store and try loading the report again.</span></div>
                    <button type="button" className={styles.retryButton} onClick={() => setRefreshKey((key) => key + 1)}>Retry</button>
                </div>
            ) : (
                <>
                    <div className={styles.sheetToolbar}>
                        <label className={styles.searchBox}>
                            <Search size={15} />
                            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search receipt, client, service…" />
                        </label>
                        <select className={styles.tableSelect} value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                            <option value="all">All methods</option><option value="cash">Cash</option><option value="upi">UPI</option>
                            <option value="card">Card</option><option value="membership">Membership</option><option value="wallet">Wallet</option>
                        </select>
                        <select className={styles.tableSelect} value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
                            <option value="all">All services</option>{serviceOptions.map(s => <option value={s} key={s}>{s}</option>)}
                        </select>
                        <select className={styles.tableSelect} value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
                            <option value="all">All staff</option>{staffOptions.map(s => <option value={s} key={s}>{s}</option>)}
                        </select>
                        <select className={styles.tableSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="all">All status</option>{statusOptions.map(s => <option value={s} key={s}>{s}</option>)}
                        </select>
                        <div className={styles.toolbarSpacer} />

                        {canExport && hasAnyExportOption && (
                            <div className={styles.exportControl} ref={exportControlRef}>
                                <button type="button" className={styles.controlButton} onClick={() => setShowExport(!showExport)} disabled={isExporting}>
                                    <Download size={14} /> {isExporting ? 'Preparing…' : 'Download'} <ChevronDown size={12} />
                                </button>
                                {showExport && (
                                    <div className={styles.exportMenu} role="menu">
                                        {canExcel && <button className={styles.exportItem} onClick={handleExcelExport}><FileSpreadsheet size={14} /><span><strong>Excel workbook</strong><small>Current visible columns</small></span></button>}
                                        {canPdf && <button className={styles.exportItem} onClick={handlePDFExport}><FileText size={14} /><span><strong>PDF report</strong><small>Current visible columns</small></span></button>}
                                        {canCsv && <button className={styles.exportItem} onClick={handleCSVExport}><FileJson size={14} /><span><strong>CSV report</strong><small>Current visible columns</small></span></button>}
                                        {canPrint && <button className={styles.exportItem} onClick={() => { setShowExport(false); window.print(); }}><Printer size={14} /><span><strong>Print report</strong><small>Sheet-friendly layout</small></span></button>}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={styles.columnControl} ref={columnControlRef}>
                            <button type="button" className={styles.controlButton} onClick={() => setShowColumns((visible) => !visible)}>
                                <SlidersHorizontal size={14} /> Columns <ChevronDown size={12} />
                            </button>
                            {showColumns && (
                                <div className={styles.columnMenu}>
                                    <strong>Visible columns</strong>
                                    {COLUMN_DEFINITIONS.map((column) => (
                                        <label key={column.key} className={styles.columnOption}>
                                            <input type="checkbox" checked={visibleColumns.includes(column.key)} onChange={() => toggleColumn(column.key)} />
                                            <span>{column.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button type="button" className={styles.resetButton} onClick={resetFilters}><RefreshCw size={14} /> Reset</button>
                    </div>

                    <div className={styles.tableWrapper}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th className={styles.rowNumber}>#</th>
                                    {orderedColumnDefinitions.map((column) => {
                                        const isSorted = sort.key === column.key;
                                        const sortIcon = isSorted ? (sort.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ChevronUp size={11} />;
                                        return (
                                            <th key={column.key} draggable="true"
                                                className={(column.align ? styles['align' + column.align[0].toUpperCase() + column.align.slice(1)] : '') + ' ' + styles.sortableHeader + (draggedColumn === column.key ? ' ' + styles.draggingHeader : '')}
                                                onClick={() => column.sortable && setSortKey(column.key)}
                                                onDragStart={(e) => handleColumnDragStart(e, column.key)}
                                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                                onDrop={(e) => handleColumnDrop(e, column.key)}
                                                onDragEnd={() => setDraggedColumn(null)}
                                            >
                                                <span>{column.label}</span>{column.sortable && sortIcon}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.length === 0 ? (
                                    <tr><td colSpan={visibleColumns.length + 1} className={styles.emptyTable}><Search size={20} /><strong>No matching transactions</strong><span>Try clearing a filter or changing the report period.</span></td></tr>
                                ) : visibleRows.map((row, index) => (
                                    <tr key={row._key}>
                                        <td className={styles.rowNumber}>{(page - 1) * pageSize + index + 1}</td>
                                        {orderedColumnDefinitions.map((column) => (
                                            <td key={column.key} className={column.align ? styles['align' + column.align[0].toUpperCase() + column.align.slice(1)] : ''}>
                                                {renderCell(row, column.key)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.tablePager}>
                        <span>Showing <strong>{firstVisibleRow}–{lastVisibleRow}</strong> of <strong>{filteredRows.length}</strong></span>
                        <label>Rows
                            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={styles.pageSizeSelect}>
                                <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                            </select>
                        </label>
                        <div className={styles.pageButtons}>
                            <button type="button" onClick={() => setPage(c => Math.max(1, c - 1))} disabled={page <= 1}><ChevronLeft size={15} /></button>
                            <span>Page {page} / {totalPages}</span>
                            <button type="button" onClick={() => setPage(c => Math.min(totalPages, c + 1))} disabled={page >= totalPages}><ChevronRight size={15} /></button>
                        </div>
                    </div>

                    <footer className={styles.fixedSummaryFooter}>
                        <div className={styles.summaryBlock}>
                            <span className={styles.blockTitle}>Revenue breakup</span>
                            <div className={styles.metricRow}><span>Net revenue</span><strong>{formatMoney(totals.totalTaxable)}</strong></div>
                            <div className={styles.metricRow}><span>Net GST collected</span><strong>{formatMoney(totals.totalGst)}</strong></div>
                        </div>
                        <div className={styles.summaryBlock}>
                            <span className={styles.blockTitle}>Payment modes</span>
                            <div className={styles.metricRow}><span>Cash</span><strong>{formatMoney(totals.totalCash)}</strong></div>
                            <div className={styles.metricRow}><span>UPI / Card</span><strong>{formatMoney(totals.totalUpi + totals.totalCard)}</strong></div>
                        </div>
                        <div className={styles.summaryBlock} style={{flex: 0.7}}>
                            <span className={styles.blockTitle}>Adjustments</span>
                            <div className={styles.metricRow}><span>Advance Adjusted</span><strong className={styles.orangeText}>{formatMoney(totals.totalMembership + totals.totalWalletUsed)}</strong></div>
                            <div className={styles.metricRow}><span>Gross Billed</span><strong>{formatMoney(totals.grandTotal)}</strong></div>
                        </div>
                        <div className={styles.grandTotalBox}>
                            <span>Net Collected</span>
                            <strong>{formatMoney(totals.actualNetCollected)}</strong>
                        </div>
                    </footer>
                </>
            )}
        </section>
    );
}